import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TableHeader } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { menuModuleDetailsData } from "./constants";
import { CompanyAccess, LanguageProxy, ModuleInputType, ModuleProxy } from "@me-team/host/main/graphql/types";
import { ModulesTagName } from "@me-team/common/src/utils/enums";
import Menu from "@me-team/common/src/images/menu.png";
import { useGetModulesListForTariffQuery } from "@me-team/host/main/Modules/graphql/modules.hooks";
import { useUpdateModulesWithRefetch } from "@me-team/common/src/hooks/useUpdateModuleRelation";
import ToastComponent from "@me-team/common/src/components/ui-components/ToastComponent/ToastComponent";
import LanguageModalForMenu from "../../ModulesList/LanguageModalForMenu/LanguageModalForMenu";
import { DeepPartial } from "@apollo/client/utilities";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const MenuModuleDetailsPage: React.FC = () => {
    const { branchId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    const canManageModules = useRoleAccess(CompanyAccess.TariffModulesManage);
    const canViewMenuList = useRoleAccess(CompanyAccess.MenuListView);

    const { data, loading, refetch } = useGetModulesListForTariffQuery({
        fetchPolicy: "network-only",
        variables: { companyId: Number(currentCompanyId) },
    });

    const modules: ModuleProxy[] = data?.user?.company?.modules;
    const MENU_MODULE: ModuleProxy = modules?.find((module: ModuleProxy) => module.name === ModulesTagName.Menu);

    const [updateModules, showToast, setShowToast] = useUpdateModulesWithRefetch(refetch);
    const updateModulesWithAccessGuard = async (companyId: number, updates: ModuleInputType[], branchId: number) => {
        if (!canManageModules) return;
        await updateModules(companyId, updates, branchId);
    };

    const onCloseLanguageModal = () => {
        setIsLanguageModalOpen(false);
    };
    if (loading) return null;

    const primaryLanguages: DeepPartial<LanguageProxy> = data.user.company.primaryLanguage;
    const languages: DeepPartial<LanguageProxy[]> = data.user.company.languages;
    const isLanguagesSubmitted = Boolean(primaryLanguages) && languages.length > 0;
    const onToggleMenuHandler = async () => {
        if (!canManageModules) return;

        if (isLanguagesSubmitted) {
            const updates: ModuleInputType[] = [{ id: MENU_MODULE.id, isActive: !MENU_MODULE.active }];
            await updateModulesWithAccessGuard(Number(currentCompanyId), updates, Number(branchId));
        } else {
            setIsLanguageModalOpen(true);
        }
    };

    return (
        <div className="mb-4">
            <TableHeader
                title={t("menu")}
                paths={[
                    {
                        path: location?.pathname,
                        label: t("Management of Tariffs"),
                    },
                    { path: `/payment/company/${currentCompanyId}/modules/branch/${branchId}`, label: t("Modules") },
                    {
                        path: location?.pathname,
                        label: t("menu"),
                    },
                ]}
            ></TableHeader>

            <div className="mt-5">
                <Row>
                    <Col xl={8} xxl className="order-2 order-xl-1 flex-grow-xxl-1">
                        {menuModuleDetailsData.map((menuItem, index) => (
                            <div
                                key={index}
                                className={`module-details_wrapper d-flex justify-content-center border rounded-24 p-4 ${menuItem.bgClass} overflow-hidden mb-8`}
                            >
                                <Row className={`align-items-center ${menuItem.reverse ? "flex-row-reverse" : ""}`}>
                                    <Col lg={6} xxl={5}>
                                        <h3 className="fs-24 text-capitalize mb-3">{t(menuItem.title)}</h3>
                                        {menuItem.items?.length ? (
                                            <ul className="custom-list ps-4 mb-lg-0">
                                                {menuItem.items.map((item, i) => (
                                                    <li key={i} className="text-grey">
                                                        {t(item.text)}
                                                        {item.subItems?.length > 0 && (
                                                            <ul className="custom-list">
                                                                {item.subItems.map((subItem, subIdx) => (
                                                                    <li key={subIdx}>{t(subItem)}</li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-grey mb-3 mb-lg-0">{t(menuItem.text)}</p>
                                        )}
                                    </Col>

                                    <Col lg={6} xxl={7} className="d-flex justify-content-center">
                                        <img
                                            className="img-fluid border rounded-12"
                                            src={menuItem.image}
                                            width={519}
                                            height={300}
                                            alt={t(menuItem.title)}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </Col>

                    <Col xl={4} className="order-1 order-xl-2 mb-8 mb-lg-5 mb-xl-0 module-details_column">
                        <div className="bg-violet rounded-24 text-center p-4 pt-8">
                            <div className="d-flex justify-content-center mb-4">
                                <img src={Menu} width={53} height={60} alt={t("Menu")} />
                            </div>
                            <h4 className="fs-5 fw-bold mb-2 lh-lg">{t("menu")}</h4>
                            <p className="fs-7 text-grey mb-0 px-2">
                                {t(
                                    'The "Menu" module allows you to create a digital QR menu with the ability to quickly update dishes, prices, and descriptions.'
                                )}
                            </p>
                            <div className="d-flex align-items-center justify-content-center gap-2 my-4 py-2 flex-wrap">
                                <span className="fs-5 fw-bold">{t("Free")}</span>
                                {MENU_MODULE.active && (
                                    <span className="badge bg-additional-green text-green rounded-1 d-inline-flex align-items-center gap-2 px-2 py-1 fs-7 fw-normal">
                                        <i className="bi bi-check2-circle"></i>
                                        {t("Connected")}
                                    </span>
                                )}
                            </div>
                            {!MENU_MODULE.active ? (
                                <Button
                                    variant="primary"
                                    className="fw-bold text-capitalize w-100"
                                    disabled={!canManageModules}
                                    onClick={onToggleMenuHandler}
                                >
                                    {t("Enable module")}
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="primary"
                                        className={`fw-bold w-100 d-flex align-items-center justify-content-center
                                       ${!canViewMenuList ? "disabled" : ""}`}
                                        aria-disabled={!canViewMenuList}
                                        tabIndex={!canViewMenuList ? -1 : undefined}
                                        onClick={(event) => {
                                            if (!canViewMenuList) {
                                                event.preventDefault();
                                                return;
                                            }
                                            navigate(`/menu/all-menus/branch/${branchId}`);
                                        }}
                                    >
                                        <i
                                            className="bi bi-list-ul me-2 d-inline-flex align-items-center lh-1"
                                            style={{ transform: "translateY(1px)" }}
                                            aria-hidden="true"
                                        />
                                        <span>{t("To the menu list")}</span>
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="p-0 border-0 text-grey text-decoration-underline d-inline-flex align-items-center gap-2 fs-7 fw-normal mt-3"
                                        disabled={!canManageModules}
                                        onClick={onToggleMenuHandler}
                                    >
                                        <i className="bi bi-power lh-0" />
                                        {t("Disable module")}
                                    </Button>
                                </>
                            )}
                            <p className={`fs-7 text-grey mb-0 ${MENU_MODULE.active ? "mt-4" : "mt-3"}`}>
                                {t("You can disable or enable the module’s functionality at any time.")}
                            </p>
                        </div>
                    </Col>
                </Row>
            </div>
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={t("Saved")} />}
            <LanguageModalForMenu
                isOpen={isLanguageModalOpen}
                onClose={onCloseLanguageModal}
                modulesList={modules}
                updateModules={updateModulesWithAccessGuard}
                canManageModules={canManageModules}
            />
        </div>
    );
};

export default MenuModuleDetailsPage;
